import React, { Component } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  CircularProgress,
  Button
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import { compose } from 'recompose';
import { withFirebase } from '../Firebase';
import { withRouter } from 'react-router-dom';

const styles = (theme) => ({
  root: {
    padding: theme.spacing(3),
    marginTop: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  successIcon: {
    fontSize: 80,
    color: theme.palette.success.main,
    marginBottom: theme.spacing(2),
  },
  statusBox: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(3),
  },
  stepper: {
    backgroundColor: 'transparent',
  },
  button: {
    marginTop: theme.spacing(3),
  }
});

const STATUS_MESSAGES = {
  'QUEUE': {
    label: 'Na Fila',
    description: 'Seu pedido foi recebido e está aguardando processamento.',
    icon: HourglassEmptyIcon,
  },
  'SHIPPED': {
    label: 'Em Entrega',
    description: 'Seu pedido está a caminho!',
    icon: LocalShippingIcon,
  },
  'DELIVERED': {
    label: 'Entregue',
    description: 'Seu pedido foi entregue com sucesso.',
    icon: CheckCircleIcon,
  },
  'CANCELED': {
    label: 'Cancelado',
    description: 'Seu pedido foi cancelado.',
    icon: null,
  }
};

class OrderTracking extends Component {
  constructor(props) {
    super(props);

    const orderId = props.match.params.orderId;
    const organizationId = props.match.params.organizationId;

    this.state = {
      orderId,
      organizationId,
      order: null,
      loading: true,
      error: null,
    };

    this.orderRef = null;
  }

  componentDidMount() {
    this.loadOrder();
  }

  componentWillUnmount() {
    if (this.orderRef) {
      this.orderRef.off();
    }
  }

  loadOrder = () => {
    const { firebase } = this.props;
    const { organizationId, orderId } = this.state;

    // Criar listener em tempo real para o pedido
    this.orderRef = firebase.db
      .ref(`organizations/${organizationId}/orders/${orderId}`);

    this.orderRef.on('value', (snapshot) => {
      const order = snapshot.val();
      
      if (order) {
        this.setState({
          order,
          loading: false,
          error: null,
        });
      } else {
        this.setState({
          order: null,
          loading: false,
          error: 'Pedido não encontrado',
        });
      }
    }, (error) => {
      console.error('Error loading order:', error);
      this.setState({
        loading: false,
        error: 'Erro ao carregar pedido',
      });
    });
  };

  getActiveStep = (status) => {
    switch (status) {
      case 'QUEUE':
        return 0;
      case 'SHIPPED':
        return 1;
      case 'DELIVERED':
        return 2;
      case 'CANCELED':
        return -1;
      default:
        return 0;
    }
  };

  handleWhatsAppClick = () => {
    const phoneNumber = '5585987387795';
    const { orderId } = this.state;
    const message = `Fiz um pedido de número ${orderId} e gostaria de mais informações.`;
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  render() {
    const { classes } = this.props;
    const { order, loading, error } = this.state;

    if (loading) {
      return (
        <Container maxWidth="md" className={classes.root}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <CircularProgress />
          </Box>
        </Container>
      );
    }

    if (error) {
      return (
        <Container maxWidth="md" className={classes.root}>
          <Paper className={classes.paper}>
            <Typography variant="h5" color="error">
              {error}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              onClick={this.handleWhatsAppClick}
              startIcon={<WhatsAppIcon />}
            >
              Contato WhatsApp
            </Button>
          </Paper>
        </Container>
      );
    }

    if (!order) {
      return (
        <Container maxWidth="md" className={classes.root}>
          <Paper className={classes.paper}>
            <Typography variant="h5">
              Pedido não encontrado
            </Typography>
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              onClick={this.handleWhatsAppClick}
              startIcon={<WhatsAppIcon />}
            >
              Contato WhatsApp
            </Button>
          </Paper>
        </Container>
      );
    }

    const status = order.status || 'QUEUE';
    const activeStep = this.getActiveStep(status);
    const statusInfo = STATUS_MESSAGES[status];
    const StatusIcon = statusInfo?.icon;

    return (
      <Container maxWidth="md" className={classes.root}>
        <Paper className={classes.paper}>
          {/* Confirmação de pagamento */}
          <CheckCircleIcon className={classes.successIcon} />
          <Typography variant="h4" gutterBottom>
            Pagamento Confirmado!
          </Typography>
          <Typography variant="body1" color="textSecondary" gutterBottom>
            Obrigado pela sua compra. Seu pedido foi recebido com sucesso.
          </Typography>

          {/* ID do pedido */}
          <Box mt={2} mb={2}>
            <Typography variant="body2" color="textSecondary">
              Número do Pedido
            </Typography>
            <Typography variant="h6">
              #{order.id}
            </Typography>
          </Box>

          {/* Status atual */}
          <Box className={classes.statusBox}>
            {StatusIcon && (
              <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
                <StatusIcon style={{ fontSize: 50, marginRight: 16 }} />
                <Box textAlign="left">
                  <Typography variant="h5">
                    {statusInfo.label}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {statusInfo.description}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* Stepper de status */}
          {status !== 'CANCELED' && (
            <Stepper activeStep={activeStep} orientation="vertical" className={classes.stepper}>
              <Step>
                <StepLabel>
                  <Typography variant="subtitle1">Na Fila</Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="textSecondary">
                    Seu pedido foi recebido e está aguardando processamento.
                  </Typography>
                </StepContent>
              </Step>
              
              <Step>
                <StepLabel>
                  <Typography variant="subtitle1">Em Entrega</Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="textSecondary">
                    Seu pedido está sendo entregue no endereço cadastrado.
                  </Typography>
                </StepContent>
              </Step>
              
              <Step>
                <StepLabel>
                  <Typography variant="subtitle1">Entregue</Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="textSecondary">
                    Pedido entregue com sucesso!
                  </Typography>
                </StepContent>
              </Step>
            </Stepper>
          )}

          {/* Detalhes do pedido */}
          <Box mt={4} textAlign="left">
            <Typography variant="h6" gutterBottom>
              Detalhes do Pedido
            </Typography>

            <Box mb={1}>
              <Typography variant="body2" color="textSecondary">
                Valor Total
              </Typography>
              <Typography variant="h6">
                R$ {parseFloat(order.total_amount || 0).toFixed(2)}
              </Typography>
            </Box>

            {order.payment_method && (
              <Box mb={1}>
                <Typography variant="body2" color="textSecondary">
                  Forma de Pagamento
                </Typography>
                <Typography variant="body1">
                  {order.payment_method}
                </Typography>
              </Box>
            )}

            {order.created && (
              <Box mb={1}>
                <Typography variant="body2" color="textSecondary">
                  Data do Pedido
                </Typography>
                <Typography variant="body1">
                  {new Date(order.created).toLocaleString('pt-BR')}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Botão WhatsApp */}
          <Button
            variant="contained"
            color="primary"
            className={classes.button}
            onClick={this.handleWhatsAppClick}
            startIcon={<WhatsAppIcon />}
          >
            Contato WhatsApp
          </Button>
        </Paper>
      </Container>
    );
  }
}

export default compose(
  withRouter,
  withFirebase,
  withStyles(styles)
)(OrderTracking);
